import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Stethoscope, Activity, Phone } from "lucide-react";
import { SPECIALTIES, DOCTOR } from "../data/content";
import SEO from "../components/SEO";

export default function Especialidad() {
  const { slug } = useParams();
  const spec = SPECIALTIES.find((s) => s.slug === slug);
  if (!spec) return <Navigate to="/" replace />;

  const currentIdx = SPECIALTIES.findIndex((s) => s.slug === slug);
  const prev = SPECIALTIES[(currentIdx - 1 + SPECIALTIES.length) % SPECIALTIES.length];
  const next = SPECIALTIES[(currentIdx + 1) % SPECIALTIES.length];
  const related = SPECIALTIES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div>
      <SEO
        title={spec.title}
        description={`${spec.short} — Atención especializada con el Dr. César S. Martínez Maldonado, psiquiatra en Xalapa, Veracruz. ${spec.description.slice(0, 100)}`}
        canonical={`/especialidades/${spec.slug}`}
        image={spec.image}
      />
      {/* Hero */}
      <section className="relative h-[380px] md:h-[460px] overflow-hidden">
        <img src={spec.image} alt={spec.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/40" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-4 pb-10 md:pb-14 w-full">
            <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition mb-4 text-sm">
              <ArrowLeft size={16} /> Volver
            </Link>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-4xl">{spec.emoji}</span>
              <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-medium border border-white/20">
                Especialidad clínica
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              {spec.title}
            </h1>
            <p className="mt-3 text-lg text-white/90 max-w-2xl">{spec.short}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                  <Activity className="text-cyan-700" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Descripción</h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-lg">{spec.description}</p>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <span className="text-amber-700 font-bold">!</span>
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Síntomas comunes</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {spec.symptoms.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl p-3">
                    <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-slate-700">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-7 border border-cyan-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-600 to-teal-600 flex items-center justify-center">
                  <Stethoscope className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Tratamiento propuesto</h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-lg">{spec.treatment}</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* CTA */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white shadow-xl">
              <h3 className="font-bold text-xl mb-2">¿Necesitas orientación?</h3>
              <p className="text-slate-300 text-sm mb-5">
                Agenda una consulta con el {DOCTOR.shortName} y recibe atención especializada.
              </p>
              <Link to="/contacto" className="block w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 rounded-xl text-center transition">
                Agendar consulta
              </Link>
              <a href={`tel:${DOCTOR.phone.replace(/\s/g, "")}`} className="mt-3 flex items-center justify-center gap-2 text-cyan-300 hover:text-cyan-100 text-sm">
                <Phone size={14} /> {DOCTOR.phone}
              </a>
            </div>

            {/* Related */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-4">Otras especialidades</h3>
              <div className="space-y-2">
                {related.map((r) => (
                  <Link key={r.slug} to={`/especialidades/${r.slug}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition">
                    <span className="text-2xl">{r.emoji}</span>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{r.title}</div>
                      <div className="text-xs text-slate-500 line-clamp-1">{r.short}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Prev/Next */}
        <div className="mt-14 flex flex-col sm:flex-row gap-4 justify-between pt-8 border-t border-slate-200">
          <Link to={`/especialidades/${prev.slug}`} className="group flex items-center gap-3 p-4 rounded-xl hover:bg-slate-50 transition">
            <ArrowLeft size={20} className="text-slate-400 group-hover:text-cyan-600 transition" />
            <div>
              <div className="text-xs text-slate-500">Anterior</div>
              <div className="font-semibold text-slate-900">{prev.title}</div>
            </div>
          </Link>
          <Link to={`/especialidades/${next.slug}`} className="group flex items-center gap-3 p-4 rounded-xl hover:bg-slate-50 transition sm:text-right">
            <div>
              <div className="text-xs text-slate-500">Siguiente</div>
              <div className="font-semibold text-slate-900">{next.title}</div>
            </div>
            <ArrowRight size={20} className="text-slate-400 group-hover:text-cyan-600 transition" />
          </Link>
        </div>
      </section>
    </div>
  );
}
